import type { Href } from 'expo-router';
import { ArrowRight } from 'lucide-react-native';
import { useEffect, useState, type ReactNode } from 'react';
import { StyleSheet, View } from 'react-native';

import { Button } from '@/components/Button';
import { Text } from '@/components/Text';
import { spacing } from '@/constants/theme';
import { inTransit } from '@/lib/socialProgress';
import type { DealCta, DealStep, StepStateEntry } from '@/types/deal';

import { DisclosureToggle } from './DisclosureToggle';
import { ShippingTracker } from './ShippingTracker';
import { SocialPendingChip } from './SocialPendingChip';
import { SocialStepBullet, type Look } from './SocialStepBullet';
import { SocialStepVideos, type VideoHandlers } from './SocialStepVideos';

export type { Look };

export type SocialStepRowProps = {
  step: DealStep;
  number: number;
  look: Look;
  entry?: StepStateEntry;
  onCtaPress?: (cta: DealCta) => void;
  ctaLoading?: boolean;
  /** The upload step's videos: stills, Replace / Remove, the hero's scroll target. */
  videoHandlers?: VideoHandlers;
  /** The deal's Track package page, for the shipping tracker's card. */
  trackHref?: Href;
  /** The upload step's own uploader: drawn in place of its button whenever that button would show. */
  uploader?: ReactNode;
  /** More of the step's details (Get paid's earnings); keeps the step open while it's there. */
  extra?: ReactNode;
  isLast: boolean;
};

/**
 * One step of the Progress list: ring, name, a bare chevron that folds it
 * (Figma: every step has one, no word beside it), then the
 * step's details (copy, the videos sent, the shipping tracker) and its button.
 * A finished step starts folded to its name and opens again with its chevron, so the
 * tracking number or a coach's feedback is never lost; one whose package is
 * still on its way, or whose video a coach still has, stays open. A wait
 * reads the same everywhere: a clock row with the server's word. The button is never folded away. A change of
 * look (a webhook moved the step) drops the athlete's manual fold, and the
 * hero's "Fix N videos" opens the step holding that video.
 */
export function SocialStepRow({ step, number, look, entry, onCtaPress, ctaLoading, videoHandlers, trackHref, uploader, extra, isLast }: SocialStepRowProps) {
  const testID = `social-step-${step.key}`;
  const [toggled, setToggled] = useState<boolean | null>(null);
  useEffect(() => setToggled(null), [look]);
  const focus = videoHandlers?.focusRequest;
  const holdsFocus = !!focus && !!entry?.videos?.some((v) => v.id === focus.videoId);
  useEffect(() => {
    if (holdsFocus) setToggled(true);
  }, [holdsFocus, focus?.n]);
  const registerVideo = videoHandlers?.registerVideo;
  const attentionId = entry?.attention?.video_id;
  const locked = look === 'locked';
  // A step not reached yet starts open, its words showing, like any step still to do.
  const open = toggled ?? (look !== 'done' || inTransit(entry) || withCoach(entry) || !!extra);
  const primary = step.ctas?.find((c) => c.kind === 'primary') ?? step.ctas?.[0];
  const pending = look === 'pending';
  // The server's words for this step right now win ("Applied", "Video rejected").
  const name = entry?.name ?? step.name;
  const copy = entry?.description ?? (pending ? step.waiting_description : undefined) ?? step.description;
  // Once there are videos the step's button adds another, quieter than a video's own Replace.
  const adding = !!entry?.add_label;
  const addHere = adding && open && look !== 'active' && primary && onCtaPress;
  // The uploader stands in for the step's button (active, or adding another), never folded away.
  const uploadHere = !!uploader && (look === 'active' || adding);

  return (
    <View
      style={styles.row}
      testID={testID}
      // The fallback target for "Fix N videos" when the card itself isn't laid out yet.
      ref={attentionId && registerVideo ? (view) => registerVideo(`step:${attentionId}`, view) : undefined}
    >
      <SocialStepBullet number={number} look={look} isLast={isLast} testID={testID} />

      <View style={[styles.body, isLast && styles.bodyLast]}>
        <View style={styles.head}>
          <Text variant="bodyBold" color={locked ? 'textMuted' : 'deepGreen'} style={styles.name}>
            {name}
          </Text>
          <DisclosureToggle
            open={open}
            onPress={() => setToggled(!open)}
            accessibilityLabel={`${open ? 'Hide' : 'See'} details for ${name}`}
            testID={`${testID}-toggle`}
          />
        </View>

        {open ? (
          <View style={styles.details} testID={`${testID}-details`}>
            <Text variant="caption" color={locked ? 'textMuted' : 'text'}>
              {copy}
            </Text>
            {entry?.progress_label ? (
              <Text variant="captionBold" color="deepGreen" testID={`${testID}-progress`}>
                {entry.progress_label}
              </Text>
            ) : null}
            {entry?.videos?.length || entry?.earlier_videos?.length ? (
              <SocialStepVideos videos={entry.videos ?? []} earlier={entry.earlier_videos} {...videoHandlers} testID={`${testID}-videos`} />
            ) : null}
            {entry?.tracker ? <ShippingTracker tracker={entry.tracker} trackHref={trackHref} testID={`${testID}-tracker`} /> : null}
            {extra}
          </View>
        ) : null}

        {uploadHere ? uploader : null}
        {addHere && !uploadHere ? (
          <Button
            label={entry?.add_label ?? primary.label}
            variant="outline"
            fullWidth
            onPress={() => onCtaPress(primary)}
            style={styles.cta}
            testID={`${testID}-add`}
          />
        ) : null}
        {look === 'active' && primary && onCtaPress && !uploadHere ? (
          <Button
            label={entry?.add_label ?? primary.label}
            variant={adding ? 'outline' : 'primary'}
            fullWidth
            trailingIcon={ArrowRight}
            loading={ctaLoading}
            onPress={() => onCtaPress(primary)}
            style={styles.cta}
            testID={`${testID}-cta`}
          />
        ) : null}

        {pending && entry?.label ? <SocialPendingChip label={entry.label} testID={`${testID}-pending`} /> : null}
      </View>
    </View>
  );
}

/** A video on this step is still with a coach (the server's pending tone): the athlete keeps seeing it. */
function withCoach(entry?: StepStateEntry): boolean {
  return !!entry?.videos?.some((v) => v.status.tone === 'pending');
}



const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  body: {
    flex: 1,
    gap: spacing.xs,
    paddingTop: spacing.xs,
    // Figma: 8pt between one step and the next.
    paddingBottom: spacing.sm,
  },
  // The last row ends at the card's own padding.
  bodyLast: {
    paddingBottom: 0,
  },
  head: {
    flexDirection: 'row',
    // Centred: a bare chevron has no text baseline to sit on.
    alignItems: 'center',
    gap: spacing.md,
  },
  name: {
    flex: 1,
  },
  details: {
    gap: spacing.xs,
  },
  cta: {
    marginTop: spacing.sm,
  },
});
